import type { GasPurePayload } from '@atlaskit/analytics-gas-types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import type { CrossJoinTargetProduct } from '../../common/types';
import type { AccessRequestCapabilityResponse } from '../types';

export const canUserRequestAccess = async (
	product: CrossJoinTargetProduct,
	cloudId: string,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	source?: string,
): Promise<boolean> => {
	const resource = `ari:cloud:${product}::site/${cloudId}`;
	const params = new URLSearchParams({
		resource,
	});

	try {
		const response = await fetch(
			`/gateway/api/invitations/v1/access-requests/capabilities?${params.toString()}`,
			{
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			},
		);

		if (!response.ok) {
			// 400 response means the user is not licensed for the product and we want to handle this case so it still caches the result.
			// For other error codes we still want to throw an error so the result is not cached.
			if (response.status === 400) {
				return false;
			} else {
				throw new Error(`Unexpected response status: ${response.status}`);
			}
		}

		const capabilities = ((await response.json()) as unknown as AccessRequestCapabilityResponse)
			?.results[resource];

		return capabilities === 'DIRECT_ACCESS' || capabilities === 'REQUEST_ACCESS';
	} catch (err) {
		// Source is required for operational events otherwise it won't fire
		if (createAnalyticsEvent && source) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'fetchError',
					actionSubject: 'capabilitiesCheck',
					source,
					attributes: {
						message: String(err),
					},
				} satisfies GasPurePayload,
			}).fire();
		}

		throw err;
	}
};
